import React, { ReactElement } from 'react';
import {
  Grid,
  GridCellProps,
  GridColumn,
  GridDataStateChangeEvent,
  GridCustomFilterCellProps,
} from '@progress/kendo-react-grid';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../store';
import '@progress/kendo-theme-bootstrap/dist/all.css';
import { DataResult, process, State } from '@progress/kendo-data-query';
import { Dispatch } from 'redux';
import { useEffect } from 'react';
import { LocalizationProvider, loadMessages } from '@progress/kendo-react-intl';

import { Box, createStyles, makeStyles, Theme } from '@material-ui/core';
import { GetString } from '../../StringResourceManager';
import esMessages from '../../../utils/gridMessageKey.json';
loadMessages(esMessages, 'fr-CA');
import { locales, LocaleInterface } from '../../AlertsGrid/GridLocale';
import { getCurrentIssueCountsByDate } from '../../../store/Devices/AssetStatesActions';
import { TrendsFilter as TrendsFilterData } from '../../../Interfaces/Devices/Trends/TrendsFilter';
import { TrendsGridFilter } from '../../../Interfaces/Devices/Trends/TrendsGridFilter';
import { Label } from '@progress/kendo-react-labels';
import {
  isNullOrEmptyString,
  dateFormat,
  getLocalDateTime,
} from '../../../utils/utils';
import { LoadingPanel } from '../../LoadingPanel';
import { DeviceIssueTrendsCSV } from '../../../Interfaces/Devices/Trends/DeviceIssueTrendsCSV';
import { CSVLink } from 'react-csv';
import clsx from 'clsx';
import { Button } from '@progress/kendo-react-buttons';
import { ColumnMenu } from '../../ColumnMenus/ColumnMenu';
import { CustomDateFilterCell } from '../../CustomDateFilterCell';
import {
  setExpandedState,
  setGroupIds,
} from '@progress/kendo-react-data-tools';
import { saveDeviceIssueTrendsGridState } from '../../../store/UserSettings/UserSettingsActions';
import { initialDeviceTrendsByDateDataState } from '../../../store/UserSettings/UserSettingsState';

// Styles
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    gridLabelsContainer: {
      margin: 10,
    },
    detailsInfoLabel: {
      marginRight: 20,
    },
    box: {
      height: 60,
      width: 1200,
      padding: theme.spacing(1),
      margin: theme.spacing(1),
    },
    spreadBox: {
      alignItems: 'flex-start',
    },
    dialogAction: {
      height: '3rem',
      padding: theme.spacing(3),
      marginRight: theme.spacing(1),
    },
    toolbarButton: {
      height: 40,
      width: 200,
      marginTop: 10,
      marginLeft: 20,
      marginBottom: 10,
    },
  }),
);

interface Props {
  date: string;
  seriesName: string;
}

const CreatedOnCell = (props: GridCellProps) => {
  const dataItem = props.dataItem;
  if (!dataItem || dataItem.CreatedOn === undefined) return <td></td>;

  return <td>{getLocalDateTime(dataItem.CreatedOn)}</td>;
};

const DeviceIssueTrendsGrid: React.FC<Props> = ({
  date,
  seriesName,
}): ReactElement => {
  const classes = useStyles();
  const dispatch: Dispatch<any> = useDispatch();

  const selectedLanguage: string = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.selectedLanguage,
  );

  const [currentLocale, setCurrentLocale] = React.useState<LocaleInterface>(
    locales[0],
  );

  const deviceTrendsFilter: TrendsFilterData = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.deviceTrendsFilter,
  );

  const currentIssuesByDate: DeviceIssueTrendsCSV[] = useSelector(
    (state: ApplicationState) => state.assetStatusesReducer.currentIssuesByDate,
  );

  const currentIssueCount: number = useSelector(
    (state: ApplicationState) => state.assetStatusesReducer.currentIssueCount,
  );

  const dataState: State = useSelector(
    (state: ApplicationState) =>
      state.userSettingsReducer.deviceTrendsByDateGridDataState,
  );

  const isFetching: boolean = useSelector(
    (state: ApplicationState) =>
      state.assetStatusesReducer.isFetchingCurrentIssueCountsByDate,
  );

  useEffect(() => {
    if (seriesName !== undefined)
      deviceTrendsFilter.AssetInfo.Category = seriesName;

    let filter: TrendsGridFilter = {
      AssetInfo: deviceTrendsFilter.AssetInfo,
      Filters: deviceTrendsFilter.Filters,
      Date: new Date(date),
      StartDate: deviceTrendsFilter.StartDate,
      EndDate: deviceTrendsFilter.EndDate,
    };

    getCurrentIssueCountsByDate(dispatch, filter);
  }, []);

  useEffect(() => {
    if (selectedLanguage == 'English') {
      setCurrentLocale(locales[0]);
    } else if (selectedLanguage == 'French') {
      setCurrentLocale(locales[1]);
    }
  }, [selectedLanguage]);

  const dateFilterCell = (props: GridCustomFilterCellProps) => {
    return (
      <CustomDateFilterCell
        gridFilterCellProps={props}
        dataState={dataState}
        fieldName="CreatedOn"
      />
    );
  };

  const issueTitle = () => {
    if (deviceTrendsFilter.AssetInfo.Value == null) return;

    var value = deviceTrendsFilter.AssetInfo.Value;
    return isNullOrEmptyString(value) ? GetString('ALL') : value;
  };

  const [csvData, setCSVData] = React.useState<DeviceIssueTrendsCSV[]>([]);
  const [collapsedState, setCollapsedState] = React.useState<string[]>([]);

  const processWithGroups = (data: any[], dataState: State): DataResult => {
    if (dataState === undefined) {
      dataState = initialDeviceTrendsByDateDataState;
    }

    const newDataState: DataResult = process(data, dataState);
    setGroupIds({ data: newDataState.data, group: dataState.group });
    return newDataState;
  };

  const [dataResult, setDataResult] = React.useState<DataResult>(
    processWithGroups(currentIssuesByDate, dataState),
  );

  useEffect(() => {
    setDataResult(processWithGroups(currentIssuesByDate, dataState));
  }, [currentIssuesByDate]);

  const newData = setExpandedState({
    data: dataResult.data,
    collapsedIds: collapsedState,
  });

  const dataStateChange = (event: GridDataStateChangeEvent) => {
    const result = processWithGroups(currentIssuesByDate, event.dataState);

    saveDeviceIssueTrendsGridState(dispatch, event.dataState);
    setDataResult(result);
  };

  const csvLinkRef = React.useRef<
    CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }
  >(null);

  const getCSVFileName = () => {
    return `${deviceTrendsFilter.AssetInfo.Category}_${
      deviceTrendsFilter.AssetInfo.Value
    }_Trends_export_${dateFormat(currentLocale.language)}`;
  };

  const processExportCSV = () => {
    let result: DeviceIssueTrendsCSV[] = [];

    currentIssuesByDate.forEach(issue => {
      let data: any = { ...issue };
      data.Date = date;
      data.CreatedOn = getLocalDateTime(issue.CreatedOn);
      result.push(data);
    });

    setCSVData(result);
  };

  useEffect(() => {
    exportCSV();
  }, [csvData]);

  const exportCSV = () => {
    if (csvData.length > 0 && csvLinkRef?.current) {
      csvLinkRef.current.link.click();
    }
  };

  return (
    <LocalizationProvider language={currentLocale.language}>
      <CSVLink
        data={csvData}
        ref={csvLinkRef}
        filename={getCSVFileName() + '.csv'}
      />
      <div className={classes.gridLabelsContainer}>
        <Label className={classes.detailsInfoLabel}>
          {GetString('DATE')} <b>{date}</b>
        </Label>
        <Label className={classes.detailsInfoLabel}>
          {GetString('CATEGORY')} <b>{deviceTrendsFilter.AssetInfo.Category}</b>
        </Label>

        <Label className={classes.detailsInfoLabel}>
          {GetString('INPUT_ISSUE')} <b>{issueTitle()}</b>
        </Label>
        <Label>
          {GetString('TOTAL_COUNT')} <b>{currentIssueCount}</b>
        </Label>
      </div>
      {isFetching && LoadingPanel}
      <Grid
        style={{ height: '650px', width: '100%' }}
        sortable={true}
        filterable={true}
        resizable={true}
        pageable={{
          buttonCount: 4,
          pageSizes: [10, 20, 50, 100, 500],
        }}
        reorderable={true}
        take={100}
        groupable={true}
        data={dataResult}
        {...dataState}
        onDataStateChange={dataStateChange}
      >
        <GridColumn
          field="Division"
          title={GetString('COLUMN_TITLE_DIVISION')}
          width="115px"
          columnMenu={ColumnMenu}
          filterable={false}
        />
        <GridColumn
          field="SiteNumber"
          title={GetString('COLUMN_TITLE_STORE')}
          width="100px"
          columnMenu={ColumnMenu}
          filterable={false}
        />
        <GridColumn
          field="LaneNumber"
          title={GetString('COLUMN_TITLE_LANE')}
          width="100px"
          columnMenu={ColumnMenu}
          filterable={false}
        />
        <GridColumn
          field="Category"
          title={GetString('COLUMN_TITLE_LANE_CATEGORY')}
          width="130px"
          columnMenu={ColumnMenu}
          filterable={false}
        />
        <GridColumn
          field="Model"
          title={GetString('MODEL')}
          width="150px"
          columnMenu={ColumnMenu}
          filterable={false}
        />
        <GridColumn
          field="Value"
          title={GetString('CURRENT_ISSUE')}
          width="150px"
          columnMenu={ColumnMenu}
          filterable={false}
        />
        <GridColumn
          field="Description"
          title={GetString('COLUMN_TITLE_DESCRIPTION')}
          width="160px"
          columnMenu={ColumnMenu}
          filterable={false}
        />
        <GridColumn
          field="FirmwareVersion"
          title={GetString('COLUMN_TITLE_FIRMWARE_VERSION')}
          width="170px"
          columnMenu={ColumnMenu}
          filterable={false}
        />
        <GridColumn
          field="SerialNumber"
          title={GetString('SERIAL_NUMBER')}
          width="130px"
          columnMenu={ColumnMenu}
          filterable={false}
        />
        <GridColumn
          field="MachineName"
          title={GetString('COLUMN_TITLE_MACHINE_NAME')}
          width="130px"
          columnMenu={ColumnMenu}
          filterable={false}
        />
        <GridColumn
          field="RegionInfo"
          title={GetString('COLUMN_TITLE_REGION_INFO')}
          width="130px"
          columnMenu={ColumnMenu}
          filterable={false}
        />
        <GridColumn
          field="CreatedOn"
          title={GetString('CREATED_ON_TITLE')}
          width="150px"
          cells={{ data: CreatedOnCell, filterCell: dateFilterCell }}
        />
        <GridColumn
          field="AgeDays"
          title={GetString('AGE_DAYS')}
          width="120px"
          columnMenu={ColumnMenu}
          filterable={false}
        />
      </Grid>
      <Box className={clsx(classes.spreadBox, classes.box)}>
        <Button
          themeColor={'primary'}
          type="submit"
          className={classes.dialogAction}
          onClick={processExportCSV}
        >
          {GetString('EXPORT_TO_CSV')}
        </Button>
      </Box>
    </LocalizationProvider>
  );
};

export default DeviceIssueTrendsGrid;
